import React, { useRef, useState } from 'react';
import { Heart, Share2, Bookmark, Play, Pause, Flag, MoreVertical, Mic, Languages, Loader2, X, MessageCircle, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getMediaUrl } from '../utils/mediaUrl';
import { shareContent, buildShareUrl, incrementShareCount } from '../utils/shareContent';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import VerifiedBadge from './VerifiedBadge';
import { usePT } from '../utils/promoteTranslations';

// Podcast categories are stored in DB as the raw French label (e.g. "Making-of").
// Translate them on display via lookup on the same `catXxx` keys used by
// creationTranslations. Falls back to raw label if key missing.
const CATEGORY_KEY_MAP = {
  'Cinéma': 'catCinema',
  'Séries': 'catSeries',
  'Making-of': 'catMaking',
  'Tech': 'catTech',
  'Musiques de films': 'catMusic',
  'Interviews': 'catInterviews',
  'Critiques': 'catReviews',
};
const translateCategory = (raw, pt) => {
  if (!raw) return '';
  const key = CATEGORY_KEY_MAP[raw];
  if (!key) return raw;
  const val = pt(`categories.${key}`);
  return (val && val !== `categories.${key}`) ? val : raw;
};

/**
 * PodcastCard — vertical card (cover on top, controls below) — meant to live
 * inside a horizontal snap-x carousel on the Studio tab.
 */
const PodcastCard = ({ podcast, onUpdate }) => {
  const kbHeight = useKeyboardHeight();
  const [isPlaying, setIsPlaying] = useState(false);
  const [liked, setLiked] = useState(podcast.is_liked || false);
  const [saved, setSaved] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [likesCount, setLikesCount] = useState(podcast.likes_count || 0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [translating, setTranslating] = useState(false);
  const [showTranslatePicker, setShowTranslatePicker] = useState(false);
  const [translateResult, setTranslateResult] = useState(null);
  const [commentsCount, setCommentsCount] = useState(podcast.comments_count || 0);
  const [sharesCount, setSharesCount] = useState(podcast.shares_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const audioRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language: podcastLang } = useLanguage();
  const pt = usePT();

  const fmt = (t) => {
    if (!t || isNaN(t)) return '0:00';
    return `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;
  };

  const togglePlay = (e) => {
    e.stopPropagation();
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying) {
      a.pause();
      setIsPlaying(false);
      return;
    }
    // Use canplay to ensure audio is ready (fixes iOS playback issues)
    const onCanPlay = () => {
      a.removeEventListener('canplay', onCanPlay);
      a.play().then(() => setIsPlaying(true)).catch(err => {
        console.error('Podcast play failed', err);
        toast.error('Lecture impossible');
        setIsPlaying(false);
      });
    };
    if (a.readyState >= 2) {
      onCanPlay();
    } else {
      a.addEventListener('canplay', onCanPlay);
      try { a.load(); } catch (_) {}
      setTimeout(() => {
        a.removeEventListener('canplay', onCanPlay);
        if (!isPlaying && a.readyState < 2) setIsPlaying(false);
      }, 5000);
    }
  };

  const handleLike = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`/api/podcasts/${podcast.id}/like`);
      setLiked(!liked);
      setLikesCount(prev => liked ? prev - 1 : prev + 1);
    } catch (_) { toast.error('Erreur'); }
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`/api/podcasts/${podcast.id}/save`);
      setSaved(!saved);
      toast.success(saved ? 'Retiré des favoris' : 'Enregistré');
    } catch (_) { toast.error('Erreur'); }
  };

  const handleReport = async () => {
    try {
      await axios.post(`/api/podcasts/${podcast.id}/report`);
      toast.success('Podcast signalé');
      setShowMenu(false);
    } catch (_) { toast.error('Erreur'); }
  };

  const handleShare = async () => {
    try {
      const result = await shareContent({
        title: podcast.title || 'Podcast',
        text: `Écoute ce podcast sur Destine : ${podcast.title}`,
        url: buildShareUrl('podcast', podcast.id),
      });
      if (result.shared && !result.cancelled) {
        await incrementShareCount('podcast', podcast.id);
        setSharesCount(s => s + 1);
      }
    } catch (_) {}
    setShowShareMenu(false);
  };

  const handleShareToStory = async () => {
    try {
      await axios.post('/api/stories/share-content', {
        content_id: podcast.id,
        content_type: 'podcast',
        text_overlay: podcast.title || 'Écoutez ce podcast !',
      });
      const newCount = await incrementShareCount('podcast', podcast.id);
      if (typeof newCount === 'number') setSharesCount(newCount);
      toast.success(pt('story.shared'));
    } catch (e) {
      toast.error(pt('story.shareError'));
    }
    setShowShareMenu(false);
  };

  const loadComments = async () => {
    try {
      const r = await axios.get(`/api/studio/podcasts/${podcast.id}/comments`);
      setComments(r.data || []);
    } catch (_) { setComments([]); }
  };

  const sendComment = async () => {
    const txt = commentText.trim();
    if (!txt) return;
    setSendingComment(true);
    try {
      const r = await axios.post(`/api/studio/podcasts/${podcast.id}/comments`, { text: txt });
      setComments(c => [...c, r.data]);
      setCommentText('');
      setCommentsCount(c => c + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commentaire impossible");
    } finally { setSendingComment(false); }
  };

  const SUPPORTED_TRANSLATIONS = [
    { code: 'fr', label: '🇫🇷 Français' },
    { code: 'en', label: '🇬🇧 Anglais' },
    { code: 'es', label: '🇪🇸 Espagnol' },
    { code: 'pt', label: '🇵🇹 Portugais' },
    { code: 'de', label: '🇩🇪 Allemand' },
    { code: 'ar', label: '🇸🇦 Arabe' },
    { code: 'zh', label: '🇨🇳 Chinois' },
    { code: 'ja', label: '🇯🇵 Japonais' },
    { code: 'hi', label: '🇮🇳 Hindi' },
    { code: 'ru', label: '🇷🇺 Russe' },
    { code: 'nl', label: '🇳🇱 Néerlandais' },
    { code: 'sw', label: '🇰🇪 Swahili' },
    { code: 'ht', label: '🇭🇹 Kreyòl ayisyen' },
  ];

  const handleTranslate = (e) => {
    if (e) { try { e.stopPropagation(); e.preventDefault(); } catch (_) {} }
    if (translating) return;
    if (!podcast.audio_url) {
      toast.error("Ce podcast n'a pas d'audio à traduire");
      return;
    }
    setShowTranslatePicker(true);
  };

  const runTranslation = async (targetLang) => {
    setShowTranslatePicker(false);
    setTranslating(true);
    try {
      const fd = new FormData();
      fd.append('audio_url', podcast.audio_url);
      fd.append('target_lang', targetLang);
      const res = await axios.post('/api/translate-voice-by-url', fd);
      if (!res?.data?.translated_text && !res?.data?.audio_base64) {
        toast.error('Traduction vide — réessayez plus tard');
        return;
      }
      setTranslateResult(res.data);
    } catch (err) {
      console.error('podcast translate failed', err);
      toast.error(err?.response?.data?.detail || "Traduction impossible");
    } finally { setTranslating(false); }
  };

  return (
    <div id={`podcast-${podcast.id}`} className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-violet-500/30 transition-all overflow-hidden flex flex-col h-full" data-testid={`podcast-card-${podcast.id}`}>
      {/* Cover */}
      <div className="relative aspect-square w-full">
        {podcast.cover_image_url ? (
          <img src={getMediaUrl(podcast.cover_image_url)} alt={podcast.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-violet-600 to-magenta-600 flex items-center justify-center">
            <Mic className="w-16 h-16 text-white/60" />
          </div>
        )}
        <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-all" data-testid={`podcast-toggle-${podcast.id}`}>
          {isPlaying ? <Pause className="w-14 h-14 text-white drop-shadow-lg" /> : <Play className="w-14 h-14 text-white drop-shadow-lg" />}
        </button>
        <div className="absolute top-2 right-2">
          <button onClick={() => setShowMenu(!showMenu)} className="p-2 bg-black/50 hover:bg-black/70 rounded-full">
            <MoreVertical className="w-4 h-4" />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 bg-gray-900 border border-white/10 rounded-xl shadow-xl z-10 w-40">
              <button onClick={handleReport} className="w-full px-3 py-2 text-left hover:bg-white/5 flex items-center gap-2 text-red-400 text-sm">
                <Flag className="w-4 h-4" /> {pt('feed.report')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-3 flex-1 flex flex-col">
        <button onClick={() => navigate(`/profile/${podcast.user_id}`)} className="flex items-center gap-2 mb-2 hover:opacity-80 text-left">
          {podcast.user?.profile_image_url ? (
            <img src={getMediaUrl(podcast.user.profile_image_url)} alt="" className="w-7 h-7 rounded-full object-cover" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-magenta-500/50 flex items-center justify-center text-[10px] font-bold">{(podcast.user?.name || '?').charAt(0).toUpperCase()}</div>
          )}
          <div className="min-w-0">
            <span className="text-sm text-white flex items-center gap-1 truncate">{podcast.user?.name}{podcast.user?.is_verified && <VerifiedBadge size="xs" />}</span>
            <span className="text-[11px] text-gray-400 truncate block">
              {podcast.category && <>{translateCategory(podcast.category, pt)}</>}
              {podcast.category && (podcast.created_at || podcast.uploaded_at) && ' · '}
              {(podcast.created_at || podcast.uploaded_at) && (() => {
                const localeMap = { fr: 'fr-FR', en: 'en-US', es: 'es-ES', ar: 'ar-SA', pt: 'pt-PT', de: 'de-DE', it: 'it-IT', ru: 'ru-RU', zh: 'zh-CN', ja: 'ja-JP', hi: 'hi-IN', nl: 'nl-NL', sw: 'sw-KE', ht: 'ht-HT' };
                const loc = localeMap[podcastLang] || 'fr-FR';
                const d = new Date(podcast.created_at || podcast.uploaded_at);
                return d.toLocaleDateString(loc, { day: 'numeric', month: 'short', year: 'numeric' })
                  + ' · '
                  + d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' });
              })()}
            </span>
          </div>
        </button>
        <h3 className="font-semibold text-sm line-clamp-2 mb-2 flex-1">{podcast.title}</h3>

        {/* Progress bar with draggable thumb (click + drag to seek) */}
        <div
          className="relative w-full bg-gray-700 rounded-full h-2 cursor-pointer group"
          data-testid={`podcast-progress-${podcast.id}`}
          onPointerDown={(e) => {
            if (!audioRef.current || !duration) return;
            const bar = e.currentTarget;
            const seek = (clientX) => {
              const rect = bar.getBoundingClientRect();
              const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
              audioRef.current.currentTime = pct * duration;
              setCurrentTime(pct * duration);
              setProgress(pct * 100);
            };
            seek(e.clientX);
            const onMove = (ev) => seek(ev.clientX);
            const onUp = () => {
              window.removeEventListener('pointermove', onMove);
              window.removeEventListener('pointerup', onUp);
            };
            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp);
          }}
        >
          <div className="absolute top-0 left-0 h-full bg-gradient-to-r from-magenta-500 to-violet-500 rounded-full" style={{ width: `${progress}%` }} />
          <div
            className="absolute top-1/2 w-3.5 h-3.5 rounded-full bg-white shadow-lg ring-2 ring-fuchsia-500 -translate-y-1/2 -translate-x-1/2 pointer-events-none"
            style={{ left: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between text-[10px] text-gray-500 mt-0.5">
          <span>{fmt(currentTime)}</span>
          <span>{fmt(duration)}</span>
        </div>
      </div>

      <div className="flex items-center justify-around border-t border-white/10 px-3 py-2">
        <button onClick={handleLike} className={`flex items-center gap-1 ${liked ? 'text-red-500' : 'text-gray-400 hover:text-red-500'}`} data-testid={`podcast-like-${podcast.id}`}>
          <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />
          <span className="text-xs">{likesCount}</span>
        </button>
        <button onClick={() => { setShowComments(true); loadComments(); }} className="flex items-center gap-1 text-gray-400 hover:text-cyan-400" data-testid={`podcast-comment-${podcast.id}`}>
          <MessageCircle className="w-4 h-4" />
          <span className="text-xs">{commentsCount}</span>
        </button>
        <button onClick={handleTranslate} disabled={translating} className="text-gray-400 hover:text-violet-300 disabled:opacity-50" data-testid={`podcast-translate-${podcast.id}`} title={pt('feed.translateVoice')}>
          {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />}
        </button>
        <div className="relative">
          <button onClick={() => setShowShareMenu(v => !v)} className="flex items-center gap-1 text-gray-400 hover:text-emerald-500" data-testid={`podcast-share-${podcast.id}`}>
            <Share2 className="w-4 h-4" />
            {sharesCount > 0 && <span className="text-xs">{sharesCount}</span>}
          </button>
          {showShareMenu && (
            <div className="absolute bottom-full right-0 mb-2 w-52 bg-gray-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
              <button onClick={handleShare} className="w-full px-3 py-2.5 text-left text-sm hover:bg-white/10 flex items-center gap-2" data-testid={`podcast-share-link-${podcast.id}`}>
                <Share2 className="w-4 h-4 text-blue-400" /> {pt('feed.share')}
              </button>
              {user && (
                <button onClick={handleShareToStory} className="w-full px-3 py-2.5 text-left text-sm hover:bg-white/10 flex items-center gap-2 border-t border-white/10" data-testid={`podcast-share-to-story-${podcast.id}`}>
                  <Heart className="w-4 h-4 text-magenta-400" /> {pt('story.sharePodcastToStory')}
                </button>
              )}
            </div>
          )}
        </div>
        <button onClick={handleSave} className={`${saved ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-500'}`} data-testid={`podcast-save-${podcast.id}`}>
          <Bookmark className={`w-4 h-4 ${saved ? 'fill-current' : ''}`} />
        </button>
      </div>

      <audio ref={audioRef} src={getMediaUrl(podcast.audio_url)} preload="metadata" onEnded={() => setIsPlaying(false)} onTimeUpdate={() => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
          if (audioRef.current.duration) setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
        }
      }} onLoadedMetadata={() => { if (audioRef.current) setDuration(audioRef.current.duration); }} />

      {showTranslatePicker && (
        <div className="fixed inset-0 z-[90] bg-black/85 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowTranslatePicker(false)}>
          <div className="bg-gray-900 rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 border border-white/10" onClick={(e) => e.stopPropagation()} data-testid={`podcast-translate-picker-${podcast.id}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Languages className="w-5 h-5 text-cyan-400" />
                Traduire en…
              </h3>
              <button onClick={() => setShowTranslatePicker(false)} className="p-1.5 hover:bg-white/10 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto">
              {SUPPORTED_TRANSLATIONS.map(l => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => runTranslation(l.code)}
                  className="px-3 py-3 bg-white/5 hover:bg-cyan-500/20 border border-white/10 hover:border-cyan-500/40 rounded-xl text-sm font-medium transition-all"
                  data-testid={`podcast-translate-target-${podcast.id}-${l.code}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {translateResult && (
        <div className="fixed inset-0 z-[90] bg-black/85 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setTranslateResult(null)}>
          <div className="w-full max-w-md bg-gradient-to-br from-violet-900 to-fuchsia-900 rounded-t-3xl sm:rounded-3xl p-5 border border-white/10 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-base flex items-center gap-2"><Languages className="w-4 h-4" /> Traduction</h3>
              <button onClick={() => setTranslateResult(null)}><X className="w-5 h-5" /></button>
            </div>
            {translateResult.translated_text && <p className="text-sm bg-black/30 rounded-xl p-3 mb-3">{translateResult.translated_text}</p>}
            {translateResult.audio_base64 && (
              <audio controls className="w-full" src={`data:audio/mpeg;base64,${translateResult.audio_base64}`} preload="auto" />
            )}
          </div>
        </div>
      )}

      {/* Comments panel */}
      {showComments && (
        <div className="fixed inset-0 z-[90] bg-black/85 flex items-end justify-center p-0" onClick={() => setShowComments(false)}>
          <div
            className="w-full max-w-md bg-gradient-to-br from-violet-900 to-fuchsia-900 rounded-t-3xl border border-white/10 max-h-[80vh] flex flex-col"
            style={{ transform: kbHeight ? `translateY(-${kbHeight}px)` : undefined, transition: 'transform 200ms ease-out', maxHeight: kbHeight ? `calc(80vh - ${kbHeight}px)` : '80vh' }}
            onClick={e => e.stopPropagation()}
            data-testid={`podcast-comments-${podcast.id}`}
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="font-bold flex items-center gap-2"><MessageCircle className="w-4 h-4" /> {pt('comments.title')}</h3>
              <button onClick={() => setShowComments(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {comments.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-6">{pt('comments.beFirst')}</p>
              ) : comments.map(c => (
                <div key={c.id} className="flex items-start gap-2" data-testid={`podcast-comment-${c.id}`}>
                  {c.user?.avatar_url ? (
                    <img src={getMediaUrl(c.user.avatar_url)} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-magenta-500/40 flex items-center justify-center text-xs font-bold shrink-0">{(c.user?.name||'?').charAt(0).toUpperCase()}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400">{c.user?.name || 'Anonyme'} {c.user?.is_verified && <VerifiedBadge size="xs" />}</p>
                    <p className="text-sm bg-white/5 rounded-xl px-3 py-2 mt-1 break-words">{c.text}</p>
                  </div>
                  {c.user_id === user?.id && (
                    <button
                      onClick={async () => {
                        if (!window.confirm('Supprimer ce commentaire ?')) return;
                        try {
                          await axios.delete(`/api/comments/${c.id}`);
                          setComments(prev => prev.filter(x => x.id !== c.id));
                          setCommentsCount(n => Math.max(0, n - 1));
                          toast.success('Commentaire supprimé');
                        } catch (err) { toast.error("Suppression impossible"); }
                      }}
                      className="p-1 text-red-300 hover:bg-red-500/20 rounded shrink-0"
                      title="Supprimer"
                      data-testid={`podcast-comment-delete-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-white/10 flex gap-2" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
              <input
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendComment(); } }}
                placeholder={pt('comments.addPh')}
                className="flex-1 px-3 py-2 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:border-magenta-500 focus:outline-none"
                data-testid={`podcast-comment-input-${podcast.id}`}
                maxLength={500}
              />
              <button onClick={sendComment} disabled={sendingComment || !commentText.trim()} className="px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 rounded-xl flex items-center" data-testid={`podcast-comment-send-${podcast.id}`}>
                {sendingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PodcastCard;
